import { byMethod } from '@oysterjs/core/graphql/actions';
import { merge } from '@oysterjs/core/graphql/loaders';
import type { RouteObject } from 'react-router-dom';
import {
  submitArchiveAgencyDesign,
  submitCreateAgencyDesign,
  submitDuplicateAgencyDesign,
  submitSetDefaultAgencyDesign,
  submitUpdateAgencyDesign
} from '~/api/data/designs/mutations';
import { loadAgencyDesigns } from '~/api/data/designs/queries';
import { SettingsDesignRoute } from '~/pages/settings/design';
import type { SettingsRouteHandle } from '~/pages/settings';
import { Action, Resource } from '~/api/graphql';
import { ProtectedRoute } from '~/components/permissions';
import { FlagGuard } from '~/components/flag-guard';
import { FEATURE_FLAGS } from '~/components/feature-flags';

/** The editor names the design in its own title, with a way back to the list. */
const EDITOR: SettingsRouteHandle = { ownPageTitle: true };

export const designRoute: RouteObject = {
  element: <FlagGuard flag={FEATURE_FLAGS.AGENCY_BRAND_DESIGN} to="/app/settings/profile" />,
  children: [
    {
      // A sibling of 'agency' rather than a child: the agency route's children render
      // inside its page through an outlet, and this is a page of its own.
      path: 'design',
      loader: merge({ designs: loadAgencyDesigns }),
      element: (
        <ProtectedRoute
          permissions={[
            { resource: Resource.HtmlDocumentTemplate, action: Action.List },
            { resource: Resource.HtmlDocumentTemplate, action: Action.Read }
          ]}
          redirectTo="/app/settings/profile"
        >
          <SettingsDesignRoute />
        </ProtectedRoute>
      ),
      // Editing is its own route rather than page state so the shell can collapse its
      // navigation for it: the panel and the document both want the width.
      children: [
        { index: true, element: null },
        {
          path: 'new',
          action: byMethod({ POST: submitCreateAgencyDesign }),
          handle: EDITOR,
          element: null
        },
        { path: 'edit', element: null },
        {
          path: ':designId/edit',
          action: byMethod({ PUT: submitUpdateAgencyDesign }),
          handle: EDITOR,
          element: null
        },
        { path: ':designId/duplicate', action: byMethod({ POST: submitDuplicateAgencyDesign }) },
        { path: ':designId/archive', action: byMethod({ POST: submitArchiveAgencyDesign }) },
        { path: ':designId/default', action: byMethod({ POST: submitSetDefaultAgencyDesign }) }
      ]
    }
  ]
};
